import { useElementMeasures } from '@hooks/useElementMeasures';
import { cn } from '@utils/cn';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsPhone } from '@hooks/useIsPhone';
import type { DrawerProps } from './types';
import {
  DrawerRoot,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerPortal,
  DrawerOverlay,
  DrawerContent,
} from '..';

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  ({ open, setOpen, title, children, className, phoneSheet }, ref) => {
    const headerHeight = useElementMeasures('header').height;
    const isPhone = useIsPhone();
    const { t } = useTranslation('app');

    if (phoneSheet && isPhone)
      return (
        <DrawerRoot open={open} onOpenChange={setOpen} direction="bottom">
          <DrawerContent
            ref={ref}
            aria-describedby={undefined}
            overlayClassName="z-[60] bg-[rgba(24,28,33,.45)]"
            className="max-h-[90dvh] rounded-t-2xl border-0 pb-[calc(24px+env(safe-area-inset-bottom,0px))] shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
            <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-neutral-200" />
            <DrawerHeader className="flex shrink-0 flex-row items-center justify-between border-b py-1.5 pl-[18px] pr-2">
              <DrawerTitle className="m-0 min-w-0 flex-1 text-base font-bold text-neutral-800">
                {title}
              </DrawerTitle>
              <DrawerClose asChild>
                <button
                  type="button"
                  aria-label={t('actions.close')}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-xl text-neutral-600 hover:bg-neutral-50">
                  <SvgConsumer id="close" />
                </button>
              </DrawerClose>
            </DrawerHeader>
            {children}
          </DrawerContent>
        </DrawerRoot>
      );

    return (
      <DrawerRoot open={open} onOpenChange={setOpen} direction="right">
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            ref={ref}
            className={cn(
              'fixed bottom-auto left-auto right-0 top-0 h-auto w-full max-w-[800px] overflow-hidden rounded-none border-b-0 border-l border-r-0 border-t-0 shadow-card transition-transform duration-150',
              className,
            )}
            style={{
              top: headerHeight,
              maxHeight: `calc(100vh - ${headerHeight}px)`,
            }}>
            <DrawerHeader className="sticky top-0 z-10 flex h-10 flex-row items-center justify-between border-b bg-white px-2 py-0">
              <DrawerTitle className="m-0 min-w-0 flex-1 text-base font-semibold text-neutral-800">
                {title}
              </DrawerTitle>
              <DrawerClose asChild>
                <button
                  className="p-1 text-xs text-neutral-300 hover:text-neutral-800"
                  type="button">
                  <SvgConsumer id="close" />
                </button>
              </DrawerClose>
            </DrawerHeader>
            {children}
          </DrawerContent>
        </DrawerPortal>
      </DrawerRoot>
    );
  },
);
